import { useState } from 'react';
import useTranslation from '../../customHooks/useTranslation';
import LoopFigure from './LoopFigure';
import { Keynote, Sheet, SheetHeading } from './Sheet';

// The keynote schedule: each numbered item is a segment of Fig. 1.
// Pointing at an item lights its segment on the drawing beside it.
export default function Scope() {
	const t = useTranslation();
	const s = t.scope;
	const [active, setActive] = useState(1);
	const [open, setOpen] = useState<number | null>(null);

	return (
		<Sheet id='scope' code='A-201' title={t.nav.sheets.scope} className='drafting-grid'>
			<div className='mx-auto grid max-w-[1320px] gap-x-12 gap-y-10 px-4 py-20 sm:px-8 lg:grid-cols-12 lg:py-28'>
				<div className='lg:col-span-5'>
					<div className='lg:sticky lg:top-24'>
						<SheetHeading id='scope'>{s.heading}</SheetHeading>
						<p className='mt-5 max-w-[46ch] text-lg leading-relaxed text-ink-soft'>{s.intro}</p>
						<figure className='mt-8 hidden lg:block'>
							<LoopFigure active={active} labels={t.hero.phases.map(p => p.name)} span={t.hero.figure.span} animateIn={false} className='w-full' />
							<figcaption className='lettering mt-2 text-label text-ink-faint'>
								{t.hero.figure.caption} · {t.hero.figure.title}
							</figcaption>
						</figure>
					</div>
				</div>

				<div className='lg:col-span-7'>
				<figure className='sticky top-16 z-10 -mx-4 border-b border-ink-line bg-sheet/95 px-4 py-3 backdrop-blur-xs sm:-mx-8 sm:px-8 lg:hidden'>
					<LoopFigure active={active} labels={t.hero.phases.map(p => p.name)} span={t.hero.figure.span} animateIn={false} className='mx-auto h-36 w-auto sm:h-48' />
					<figcaption className='lettering mt-1 text-center text-label text-redline'>
						{active} · {t.hero.phases[active - 1].name}
					</figcaption>
				</figure>
				<ol className='border-t border-ink-line'>
					{s.items.map((item, i) => {
						const n = i + 1;
						const isActive = active === n;
						const isOpen = open === n;
						const panelId = `scope-panel-${n}`;
						return (
							<li key={item.name} className={`border-b border-ink-line transition-colors duration-300 ${isActive ? 'bg-sheet-deep/70' : ''}`} onMouseEnter={() => setActive(n)} onFocusCapture={() => setActive(n)} onClick={() => setActive(n)}>
								<div className='grid grid-cols-[auto_1fr] gap-x-5 px-1 py-6 sm:px-4'>
									<Keynote n={n} active={isActive} className='mt-0.5' />
									<div>
										<div className='flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1'>
											<h3 className='type-wide text-xl font-bold tracking-[-0.015em] text-ink sm:text-2xl'>{item.name}</h3>
											<span className={`lettering text-label transition-colors ${isActive ? 'text-redline' : 'text-ink-faint'}`}>{t.hero.phases[i].name}</span>
										</div>
										<p className='mt-2 max-w-[62ch] leading-relaxed text-ink-soft'>{item.summary}</p>

										<div id={panelId} hidden={!isOpen} className='mt-4'>
											<ul className='space-y-2'>
												{item.details.map(d => (
													<li key={d} className='grid grid-cols-[0.75rem_1fr] gap-3 text-ink'>
														<span aria-hidden='true' className='dash-marker mt-[0.75em]' />
														<span className='max-w-[62ch] leading-relaxed'>{d}</span>
													</li>
												))}
											</ul>
										</div>

										<div className='mt-4 flex flex-wrap items-center gap-x-5 gap-y-3'>
											<button type='button' aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpen(isOpen ? null : n)} className='inline-flex items-center gap-2 text-sm font-semibold text-ink underline decoration-ink-line underline-offset-4 transition-colors hover:decoration-redline'>
												{isOpen ? s.less : s.more}
												<svg viewBox='0 0 12 12' className={`h-3 w-3 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} aria-hidden='true'>
													<path d='M2 4.5 6 8.5l4-4' fill='none' stroke='currentColor' strokeWidth='1.5' />
												</svg>
											</button>
											{item.tools.length > 0 && (
												<p className='flex flex-wrap items-center gap-1.5'>
													<span className='sr-only'>{s.tools}: </span>
													{item.tools.map(tool => (
														<span key={tool} className='lettering border border-ink-line px-1.5 py-0.5 text-[0.66rem] text-ink-soft'>
															{tool}
														</span>
													))}
												</p>
											)}
										</div>
									</div>
								</div>
							</li>
						);
					})}
				</ol>
				</div>
			</div>
		</Sheet>
	);
}
